import { FormEvent, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { classNames, formatCLP } from '../lib/format';
import type { Product } from '../types';

const PAYMENT_METHODS = [
  { id: 'cash', label: 'Efectivo' },
  { id: 'transfer', label: 'Transferencia bancaria' },
  { id: 'card', label: 'Tarjeta presencial' },
  { id: 'mp_link', label: 'Link MercadoPago' },
  { id: 'other', label: 'Otro' }
];

export function SalesPanel({ products }: { products: Product[]; reload: () => Promise<void> }) {
  const [items, setItems] = useState<Array<{ productId: string; variantId?: string; quantity: number }>>([]);
  const [pickerSearch, setPickerSearch] = useState('');
  const [customer, setCustomer] = useState({ name: '', email: '', phone: '', rut: '' });
  const [shipping, setShipping] = useState({ region: '', city: '', address: '', notes: '' });
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [status, setStatus] = useState<'paid' | 'pending' | 'preparing' | 'shipped' | 'delivered'>('paid');
  const [shippingTotal, setShippingTotal] = useState(0);
  const [discountTotal, setDiscountTotal] = useState(0);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!products.length) return;
  }, [products]);

  const filteredProducts = useMemo(() => {
    const lower = pickerSearch.toLowerCase().trim();
    if (!lower) return products.slice(0, 12);
    return products.filter((p) => p.name.toLowerCase().includes(lower) || p.categories.some((c) => c.toLowerCase().includes(lower))).slice(0, 12);
  }, [products, pickerSearch]);

  const lines = useMemo(() => items.map((item) => {
    const product = products.find((p) => p.id === item.productId);
    if (!product) return null;
    const variant = item.variantId ? product.variants.find((v) => v.id === item.variantId) : product.variants[0];
    const unit = variant?.priceOverride || product.price;
    return { product, variant, quantity: item.quantity, unit, total: unit * item.quantity };
  }).filter(Boolean) as Array<{ product: Product; variant?: Product['variants'][0]; quantity: number; unit: number; total: number }>, [items, products]);

  const subtotal = lines.reduce((sum, l) => sum + l.total, 0);
  const total = Math.max(0, subtotal + shippingTotal - discountTotal);

  function addItem(product: Product) {
    const variant = product.variants.find((v) => v.status === 'active') || product.variants[0];
    setItems((prev) => {
      const key = `${product.id}:${variant?.id || ''}`;
      const existing = prev.find((i) => `${i.productId}:${i.variantId || ''}` === key);
      if (existing) return prev.map((i) => i === existing ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { productId: product.id, variantId: variant?.id, quantity: 1 }];
    });
  }

  function setQuantity(index: number, qty: number) {
    setItems((prev) => prev.flatMap((item, i) => i === index ? (qty > 0 ? [{ ...item, quantity: qty }] : []) : [item]));
  }

  function changeVariant(index: number, variantId: string) {
    setItems((prev) => prev.map((item, i) => i === index ? { ...item, variantId } : item));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (lines.length === 0) {
      setMessage({ tone: 'error', text: 'Agrega al menos un producto' });
      return;
    }
    setSubmitting(true);
    setMessage(null);
    try {
      const result = await api.admin.createManualOrder({
        customer,
        shipping,
        items,
        paymentMethod,
        status,
        notes,
        shippingTotal,
        discountTotal
      });
      setMessage({ tone: 'ok', text: `✓ Pedido #${result.orderNumber} creado.` });
      setItems([]);
      setCustomer({ name: '', email: '', phone: '', rut: '' });
      setShipping({ region: '', city: '', address: '', notes: '' });
      setShippingTotal(0);
      setDiscountTotal(0);
      setNotes('');
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'Error' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="dashboard-stack">
      <section className="admin-card">
        <div className="admin-card-head">
          <div>
            <h2>Nueva venta manual</h2>
            <p className="muted">Para pedidos por WhatsApp, ferias, mayorista o presencial.</p>
          </div>
          <button className="primary-button compact" disabled={submitting}>{submitting ? 'Guardando...' : `Crear pedido · ${formatCLP(total)}`}</button>
        </div>

        <div className="sales-layout">
          <div>
            <h3>Productos</h3>
            <input type="search" placeholder="Buscar producto..." value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)} />
            <div className="product-picker">
              {filteredProducts.map((p) => (
                <button key={p.id} type="button" className="product-pick" onClick={() => addItem(p)}>
                  <div className="product-pick-thumb">
                    {p.images[0]?.url ? <img src={p.images[0].url} alt="" /> : <span>{p.name.slice(0, 2).toUpperCase()}</span>}
                  </div>
                  <div>
                    <strong>{p.name}</strong>
                    <small>{formatCLP(p.price)}</small>
                  </div>
                  <span>+</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <h3>Carro ({lines.length})</h3>
            {lines.length === 0 ? (
              <p className="muted">Sin productos. Agrega desde la lista de la izquierda.</p>
            ) : (
              <div className="sales-cart">
                {lines.map((line, i) => (
                  <div key={i} className="sales-cart-row">
                    <div>
                      <strong>{line.product.name}</strong>
                      {line.product.variants.length > 1 && (
                        <select value={line.variant?.id || ''} onChange={(e) => changeVariant(i, e.target.value)}>
                          {line.product.variants.map((v) => (
                            <option key={v.id} value={v.id}>{[v.color, v.size].filter(Boolean).join(' / ')} (stock {v.stock})</option>
                          ))}
                        </select>
                      )}
                    </div>
                    <div className="sales-qty">
                      <button type="button" onClick={() => setQuantity(i, line.quantity - 1)}>−</button>
                      <span>{line.quantity}</span>
                      <button type="button" onClick={() => setQuantity(i, line.quantity + 1)}>+</button>
                    </div>
                    <strong>{formatCLP(line.total)}</strong>
                    <button type="button" className="link-button" onClick={() => setQuantity(i, 0)}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="admin-card">
        <h2>Cliente y despacho</h2>
        <div className="form-grid">
          <label>
            Nombre <em>obligatorio</em>
            <input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} required />
          </label>
          <label>
            Email
            <input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} />
          </label>
          <label>
            Teléfono
            <input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} />
          </label>
          <label>
            RUT
            <input value={customer.rut} onChange={(e) => setCustomer({ ...customer, rut: e.target.value })} />
          </label>
          <label className="span-2">
            Dirección de despacho
            <input value={shipping.address} onChange={(e) => setShipping({ ...shipping, address: e.target.value })} placeholder="(opcional si retira en local)" />
          </label>
          <label>Comuna<input value={shipping.city} onChange={(e) => setShipping({ ...shipping, city: e.target.value })} /></label>
          <label>Región<input value={shipping.region} onChange={(e) => setShipping({ ...shipping, region: e.target.value })} /></label>
        </div>
      </section>

      <section className="admin-card">
        <h2>Pago y resumen</h2>
        <div className="form-grid">
          <label>
            Método de pago
            <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              {PAYMENT_METHODS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </label>
          <label>
            Estado inicial
            <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
              <option value="paid">Pagado</option>
              <option value="pending">Pendiente de pago</option>
              <option value="preparing">En preparación</option>
              <option value="shipped">Enviado</option>
              <option value="delivered">Entregado</option>
            </select>
          </label>
          <label>
            Despacho cobrado (CLP)
            <input type="number" min={0} value={shippingTotal} onChange={(e) => setShippingTotal(Number(e.target.value))} />
          </label>
          <label>
            Descuento (CLP)
            <input type="number" min={0} value={discountTotal} onChange={(e) => setDiscountTotal(Number(e.target.value))} />
          </label>
          <label className="span-2">
            Notas internas
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ej: Cliente compró en feria, quiere boleta a su RUT" />
          </label>
        </div>

        <dl className="sales-totals">
          <div><dt>Subtotal</dt><dd>{formatCLP(subtotal)}</dd></div>
          <div><dt>Despacho</dt><dd>{formatCLP(shippingTotal)}</dd></div>
          {discountTotal > 0 && <div><dt>Descuento</dt><dd>-{formatCLP(discountTotal)}</dd></div>}
          <div className="total"><dt>Total</dt><dd>{formatCLP(total)}</dd></div>
        </dl>
      </section>

      {message && <p className={classNames('form-message', `tone-${message.tone}`)}>{message.text}</p>}
    </form>
  );
}
